import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  MessageCircle, Dumbbell, Trophy, Target, X,
} from 'lucide-react';
import { toast } from 'sonner';

import { PostHeader } from './PostHeader';
import { PostContent } from './PostContent';
import { PostActions } from './PostActions';
import { PostComments } from './PostComments';

const POST_TYPE_CONFIG = {
  workout: { icon: Dumbbell, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Workout' },
  achievement: { icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Achievement' },
  milestone: { icon: Target, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Milestone' },
  challenge: { icon: Target, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Challenge' },
  general: { icon: MessageCircle, color: 'text-gray-400', bg: 'bg-gray-500/20', label: 'Post' },
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const diff = Math.floor((new Date() - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const PostCard = ({ post, api, currentUserId, onPostUpdate, onPostDelete }) => {
  const [liked, setLiked] = useState(post.user_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(post.comments || []);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [editPostType, setEditPostType] = useState(post.post_type || 'general');
  const [saving, setSaving] = useState(false);
  const [currentContent, setCurrentContent] = useState(post.content);
  const [currentPostType, setCurrentPostType] = useState(post.post_type || 'general');
  const [isEditedFlag, setIsEditedFlag] = useState(post.is_edited || false);

  const config = POST_TYPE_CONFIG[currentPostType] || POST_TYPE_CONFIG.general;
  const PostIcon = config.icon;

  const handleStartEdit = () => {
    setEditContent(currentContent);
    setEditPostType(currentPostType);
    setIsEditing(true);
    setShowMenu(false);
  };

  const handleCancelEdit = () => {
    setEditContent(currentContent);
    setEditPostType(currentPostType);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) {
      toast.error('Post cannot be empty');
      return;
    }
    setSaving(true);
    try {
      await api().put(`/social/posts/${post.id}`, {
        content: editContent.trim(),
        post_type: editPostType,
      });
      setCurrentContent(editContent.trim());
      setCurrentPostType(editPostType);
      setIsEditedFlag(true);
      setIsEditing(false);
      toast.success('Post updated');
      if (onPostUpdate) onPostUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update post');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    setDeleting(true);
    try {
      await api().delete(`/social/posts/${post.id}`);
      toast.success('Post deleted');
      if (onPostDelete) onPostDelete(post.id);
    } catch {
      toast.error('Failed to delete post');
    }
    setDeleting(false);
    setShowMenu(false);
  };

  const handleLike = async () => {
    try {
      if (liked) {
        await api().delete(`/social/posts/${post.id}/like`);
        setLiked(false);
        setLikesCount((prev) => prev - 1);
      } else {
        await api().post(`/social/posts/${post.id}/like`);
        setLiked(true);
        setLikesCount((prev) => prev + 1);
      }
    } catch {
      toast.error('Failed to update like');
    }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await api().post(`/social/posts/${post.id}/comments`, { content: newComment });
      setComments((prev) => [...prev, res.data.comment]);
      setNewComment('');
      if (onPostUpdate) onPostUpdate();
    } catch {
      toast.error('Failed to add comment');
    }
    setSubmitting(false);
  };

  const handleShare = async (platform) => {
    const shareUrl = `${window.location.origin}/social/post/${post.id}`;
    const shareText = `Check out this post on Ascend & Surpass: "${post.content.substring(0, 100)}${post.content.length > 100 ? '...' : ''}"`;

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        return false;
      }
    };

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({ title: 'Ascend & Surpass Post', text: shareText, url: shareUrl });
        toast.success('Shared successfully!');
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    const textToCopy = platform === 'copy' ? shareUrl : `${shareText}\n\n${shareUrl}`;
    const copied = await copyToClipboard(textToCopy);
    if (copied) {
      toast.success(platform === 'copy' ? 'Link copied!' : 'Copied for sharing!', {
        description: 'Paste it on your favorite platform',
        duration: 3000,
      });
    } else {
      toast.info('Copy this link:', { duration: 5000, description: shareUrl });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border overflow-hidden rounded-2xl"
    >
      <PostHeader
        post={post} config={config} PostIcon={PostIcon}
        isOwner={post.user_id === currentUserId}
        showMenu={showMenu} setShowMenu={setShowMenu}
        onStartEdit={handleStartEdit} onDelete={handleDelete}
        deleting={deleting} formatTimeAgo={formatTimeAgo}
      />
      <PostContent
        post={post} currentContent={currentContent} currentPostType={currentPostType}
        isEditedFlag={isEditedFlag}
        isEditing={isEditing} editContent={editContent} setEditContent={setEditContent}
        editPostType={editPostType} setEditPostType={setEditPostType}
        onSave={handleSaveEdit} onCancel={handleCancelEdit} saving={saving}
        postTypeConfig={POST_TYPE_CONFIG}
      />
      <PostActions
        liked={liked} likesCount={likesCount}
        commentsCount={post.comments_count || comments.length}
        onLike={handleLike}
        onToggleComments={() => setShowComments(!showComments)}
        onShare={handleShare}
      />
      {showComments && (
        <PostComments
          comments={comments} newComment={newComment} setNewComment={setNewComment}
          onSubmit={handleComment} submitting={submitting}
        />
      )}
    </motion.div>
  );
};

export const CreatePostModal = ({ isOpen, onClose, api, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }
    setSubmitting(true);
    try {
      await api().post('/social/posts', { content, post_type: postType });
      toast.success('Post created!');
      setContent('');
      setPostType('general');
      onClose();
      if (onPostCreated) onPostCreated();
    } catch {
      toast.error('Failed to create post');
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card border border-border p-6 max-w-lg w-full rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-white uppercase tracking-wider">Create Post</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex gap-2">
            {['general', 'milestone'].map((type) => (
              <Button
                key={type}
                variant={postType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPostType(type)}
                className={postType === type ? 'bg-primary text-black' : ''}
              >
                {POST_TYPE_CONFIG[type].label}
              </Button>
            ))}
          </div>

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind? Share your fitness journey..."
            className="bg-black border-border text-white min-h-[120px]"
          />

          <Button onClick={handleSubmit} disabled={!content.trim() || submitting} className="w-full bg-primary text-black">
            {submitting ? 'Posting...' : 'Post'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
